import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  Link as MuiLink,
  Grid,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useFormContext } from '../context/FormContext';
import { StepProps } from '../types';
import { getInvoiceFiles } from '../utils/invoiceFiles';

// DisplayFile captures the display-relevant fields from FormFile for this component.
type DisplayFile = { name?: string; originalName?: string; filename?: string };

const SubmitConfirmation: React.FC<StepProps> = ({ onPrevious, onSubmitSuccess }) => {
  const { formData, submitForm, submitSuccess } = useFormContext();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [confirmationNumber, setConfirmationNumber] = useState<string>('');

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const result = await submitForm();
      setSubmitResult(result);
      
      if (result.success) {
        // Use the confirmation ID returned from the server
        if (result.confirmationId) {
          setConfirmationNumber(result.confirmationId);
        } else {
          // Fallback in case confirmationId is not provided
          console.warn('No confirmation ID received from server');
          setConfirmationNumber(`WS-${Math.random().toString(36).substring(2, 10).toUpperCase()}`);
        }
        
        // Notify parent component that submission was successful
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      }
    } catch (error) {
      setSubmitResult({ 
        success: false, 
        error: error instanceof Error ? error.message : 'An unknown error occurred'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-submit if this is a cancelled job - use a ref to prevent double submissions
  const hasAutoSubmitted = React.useRef(false);

  useEffect(() => {
    if (formData.isCancelled && !submitSuccess && !isSubmitting && !submitResult && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      handleSubmit();
    }
  // ESLINT NOTE: Intentionally omitting handleSubmit — it's an inline function with a new reference every render,
  // so adding it would cause this effect to re-fire on every render. The hasAutoSubmitted ref
  // already prevents double-submission; this effect only needs to react to the cancellation/state flags.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.isCancelled, submitSuccess, isSubmitting, submitResult]);

  // Helper function to format time
  const formatTime = (hour: string, minute: string, ampm: string) => {
    if (!hour || !minute || !ampm) return '';
    return `${hour}:${minute} ${ampm}`;
  };

  // Helper function to get deponent name by ID
  const getDeponentName = (deponentId: string): string => {
    const deponent = formData.deponents.find(d => d.id === deponentId);
    return deponent ? deponent.name : 'Unknown Deponent';
  };

  // Helper function to get order type description
  const getOrderTypeDescription = (orderType: string): string => {
    switch(orderType) {
      case 'Original': return 'Original';
      case 'Copy': return 'Copy';
      case 'FREE Copy': return 'FREE Copy';
      case 'Copy Estimate Only': return 'Copy Estimate Only';
      case 'Read & Sign Only': return 'Read & Sign Only';
      case 'No Order': return 'No Order';
      default: return orderType;
    }
  };

  return (
    <Card>
      <CardContent>
        {!submitSuccess && !submitResult ? (
          // Ready to submit state
          <>
            <Typography variant="h2" component="h2" gutterBottom>
              {formData.isCancelled ? 'Confirm Cancellation' : 'Review & Submit'}
            </Typography>
            
            {formData.isCancelled ? (
              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" paragraph>
                  You are confirming that job #{formData.jobNumber} was cancelled.
                </Typography>
                
                {formData.cancellationReason && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                    <Typography variant="h3" component="h3" gutterBottom>
                      Cancellation Reason:
                    </Typography>
                    <Typography variant="body1">
                      {formData.cancellationReason}
                    </Typography>
                  </Paper>
                )}
              </Box>
            ) : (
              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" paragraph>
                  You&apos;re about to submit the Reporter Worksheet for job #{formData.jobNumber}.
                  Please review all information before submitting.
                </Typography>
                
                {/* Prominent expedited notice if any transcript order is expedited */}
                {formData.attorneys.some(attorney => 
                  attorney.transcriptOrders.some(order => order.deliveryType === 'Expedited')
                ) && (
                  <Alert 
                    severity="warning" 
                    variant="filled" 
                    sx={{ 
                      mb: 2, 
                      fontWeight: 'bold',
                      '& .MuiAlert-icon': {
                        fontSize: '1.5rem'
                      }
                    }}
                  >
                    This job contains EXPEDITED transcript orders
                  </Alert>
                )}
                
                {/* Basic Job Information Section */}
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h3" component="h3" gutterBottom>
                    Job Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Job Number:</strong> {formData.jobNumber}</Typography>} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Date:</strong> {formData.jobDate !== "" ? new Date(formData.jobDate + "T12:00:00").toLocaleDateString() : ""}</Typography>} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Reporter:</strong> {formData.reporterName}</Typography>} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Reporter Email:</strong> {formData.reporterEmail}</Typography>} 
                          />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Case:</strong> {formData.caseName || 'Not specified'}</Typography>} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>Job Type:</strong> {formData.jobType}</Typography>} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary={<Typography><strong>State:</strong> {formData.jobState}</Typography>} 
                          />
                        </ListItem>
                        {/* Computed expedited status based on attorney orders */}
                        {formData.attorneys.some(attorney => 
                          attorney.transcriptOrders.some(order => order.deliveryType === 'Expedited')
                        ) && (
                          <ListItem>
                            <ListItemText 
                              primary={
                                <Typography sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                  <strong>EXPEDITED ORDER</strong>
                                </Typography>
                              } 
                            />
                          </ListItem>
                        )}
                      </List>
                    </Grid>
                  </Grid>
                </Paper>
                
                {/* Deponents Section - with Detailed Information */}
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h3" component="h3" gutterBottom>
                    Deponents ({formData.deponents.length})
                  </Typography>
                  
                  {formData.deponents.length === 0 ? (
                    <Typography variant="body2">No deponents added.</Typography>
                  ) : (
                    formData.deponents.map((deponent, index) => (
                      <Box key={deponent.id} sx={{ mb: 3, pb: 2, borderBottom: index < formData.deponents.length - 1 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none' }}>
                        <Typography variant="h4" component="h4" gutterBottom>
                          {deponent.name}
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <List dense>
                              {deponent.exhibitStatus && (
                                <ListItem>
                                  <ListItemText
                                    primary={<Typography><strong>Exhibit Status:</strong> {deponent.exhibitStatus}</Typography>}
                                  />
                                </ListItem>
                              )}
                              {deponent.exhibitCarrier && (
                                <ListItem>
                                  <ListItemText
                                    primary={<Typography><strong>Exhibit Carrier:</strong> {deponent.exhibitCarrier}</Typography>}
                                  />
                                </ListItem>
                              )}
                              {deponent.exhibitTrackingNumber && (
                                <ListItem>
                                  <ListItemText
                                    primary={<Typography><strong>Exhibit Tracking Number:</strong> {deponent.exhibitTrackingNumber}</Typography>}
                                  />
                                </ListItem>
                              )}
                              {deponent.numPages > 0 && (
                                <ListItem>
                                  <ListItemText 
                                    primary={<Typography><strong>Number of Pages:</strong> {deponent.numPages}</Typography>} 
                                  />
                                </ListItem>
                              )}
                              <ListItem>
                                <ListItemText 
                                  primary={
                                    <Typography>
                                      <strong>Read & Sign:</strong> {
                                        deponent.readAndSign === undefined ? 'Not specified' : 
                                        deponent.readAndSign ? 'Yes' : 'No'
                                      }
                                      {deponent.readAndSign === true && deponent.sendErrataTo && ` - Send To: ${deponent.sendErrataTo}`}
                                    </Typography>
                                  } 
                                />
                              </ListItem>
                              {deponent.specialProductionNotes && (
                                <ListItem>
                                  <ListItemText 
                                    primary={<Typography><strong>Special Production Notes:</strong> {deponent.specialProductionNotes}</Typography>} 
                                  />
                                </ListItem>
                              )}
                            </List>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <List dense>
                              {deponent.startTimeHour && deponent.startTimeMinute && deponent.startTimeAmPm && (
                                <ListItem>
                                  <ListItemText 
                                    primary={
                                      <Typography>
                                        <strong>Start Time:</strong> {formatTime(deponent.startTimeHour, deponent.startTimeMinute, deponent.startTimeAmPm)}
                                      </Typography>
                                    } 
                                  />
                                </ListItem>
                              )}
                              {deponent.endTimeHour && deponent.endTimeMinute && deponent.endTimeAmPm && (
                                <ListItem>
                                  <ListItemText 
                                    primary={
                                      <Typography>
                                        <strong>End Time:</strong> {formatTime(deponent.endTimeHour, deponent.endTimeMinute, deponent.endTimeAmPm)}
                                      </Typography>
                                    } 
                                  />
                                </ListItem>
                              )}
                              <ListItem>
                                <ListItemText 
                                  primary={
                                    <Typography>
                                      <strong>Transcript Type:</strong> {deponent.transcriptType === 'standard' ? 'Standard' : 
                                        deponent.transcriptType === 'realLegal' ? 'RealLegal' : 
                                        deponent.transcriptType === 'noTranscribe' ? 'No Transcribe' : 'Standard'}
                                    </Typography>
                                  } 
                                />
                              </ListItem>
                            </List>
                          </Grid>
                        </Grid>
                        
                        {/* Additional Services */}
                        {(deponent.techExpert || deponent.video || deponent.interpreter || deponent.afterHours || deponent.weekendHoliday) && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Additional Services:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {deponent.techExpert && <Chip label="Tech/Expert" size="small" color="secondary" />}
                              {deponent.video && <Chip label="Video" size="small" color="secondary" />}
                              {deponent.interpreter && <Chip label="Interpreter" size="small" color="secondary" />}
                              {deponent.afterHours && <Chip label="After-hours" size="small" color="secondary" />}
                              {deponent.weekendHoliday && <Chip label="Weekend/Holiday" size="small" color="secondary" />}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Display uploaded files */}
                        <Box sx={{ mt: 1 }}>
                          {deponent.transcriptFiles && (deponent.transcriptFiles as DisplayFile[]).length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Transcript Files:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(deponent.transcriptFiles as DisplayFile[]).map((file, fileIndex) => (
                                  <Chip 
                                    key={`transcript-${fileIndex}`} 
                                    label={file.name || file.originalName || file.filename || `File ${fileIndex + 1}`} 
                                    size="small" 
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          
                          {deponent.exhibitFiles && (deponent.exhibitFiles as DisplayFile[]).length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Exhibit Files:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(deponent.exhibitFiles as DisplayFile[]).map((file, fileIndex) => (
                                  <Chip 
                                    key={`exhibit-${fileIndex}`} 
                                    label={file.name || file.originalName || file.filename || `File ${fileIndex + 1}`} 
                                    size="small" 
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          
                          {deponent.audioFiles && (deponent.audioFiles as DisplayFile[]).length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Audio Files:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(deponent.audioFiles as DisplayFile[]).map((file, fileIndex) => (
                                  <Chip 
                                    key={`audio-${fileIndex}`} 
                                    label={file.name || file.originalName || file.filename || `File ${fileIndex + 1}`} 
                                    size="small" 
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))
                  )}
                </Paper>
                
                {/* Attorneys Section */}
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h3" component="h3" gutterBottom>
                    Attorneys ({formData.attorneys.length})
                  </Typography>
                  
                  {formData.attorneys.length === 0 ? (
                    <Typography variant="body2">No attorneys added.</Typography>
                  ) : (
                    formData.attorneys.map((attorney, index) => (
                      <Box key={attorney.id} sx={{ mb: 3, pb: 2, borderBottom: index < formData.attorneys.length - 1 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none' }}>
                        <Typography variant="h4" component="h4" gutterBottom>
                          {attorney.name}
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <List dense>
                              <ListItem>
                                <ListItemText 
                                  primary={<Typography><strong>Email:</strong> {attorney.email}</Typography>} 
                                />
                              </ListItem>
                              {attorney.firm && (
                                <ListItem>
                                  <ListItemText 
                                    primary={<Typography><strong>Firm:</strong> {attorney.firm}</Typography>} 
                                  />
                                </ListItem>
                              )}
                              {attorney.mailingAddress && (
                                <ListItem>
                                  <ListItemText 
                                    primary={<strong>Mailing Address:</strong>}
                                    secondary={attorney.mailingAddress}
                                  />
                                </ListItem>
                              )}
                              {attorney.telephonic && (
                                <ListItem>
                                  <ListItemText 
                                    primary={<Typography><strong>Attendance:</strong> Telephonic</Typography>} 
                                  />
                                </ListItem>
                              )}
                            </List>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Transcript Orders:</Typography>
                            {attorney.transcriptOrders && attorney.transcriptOrders.length > 0 ? (
                              <List dense>
                                {attorney.transcriptOrders
                                  .map((order, orderIndex) => (
                                    <ListItem key={orderIndex} sx={{ display: 'block', py: 1 }}>
                                      <Box>
                                        <Typography>
                                          <strong>{getDeponentName(order.deponentId)}:</strong> {getOrderTypeDescription(order.orderType)}
                                          {order.transcriptFormat ? ` (Format: ${order.transcriptFormat})` : ''}
                                        </Typography>
                                        
                                        {order.specialNote && (
                                          <Typography variant="body2" color="text.secondary">
                                            Special Note: {order.specialNote}
                                          </Typography>
                                        )}

                                        {/* Order submission details */}
                                        {order.orderSubmissionMethod && (
                                          <Typography variant="body2" color="text.secondary">
                                            Submission Method: {order.orderSubmissionMethod}
                                          </Typography>
                                        )}

                                        {(order.orderSubmissionPageNumber || order.orderSubmissionLineNumber) && (
                                          <Typography variant="body2" color="text.secondary">
                                            {order.orderSubmissionPageNumber && `Page: ${order.orderSubmissionPageNumber}`}
                                            {order.orderSubmissionPageNumber && order.orderSubmissionLineNumber && ', '}
                                            {order.orderSubmissionLineNumber && `Line: ${order.orderSubmissionLineNumber}`}
                                          </Typography>
                                        )}

                                        {/* Order submission files */}
                                        {order.orderSubmissionFiles && (order.orderSubmissionFiles as DisplayFile[]).length > 0 && (
                                          <Box sx={{ mt: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                              Order Files:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                              {(order.orderSubmissionFiles as DisplayFile[]).map((file, fileIndex) => (
                                                <Chip
                                                  key={`order-file-${fileIndex}`}
                                                  label={file.name || file.originalName || file.filename || `File ${fileIndex + 1}`}
                                                  size="small"
                                                  variant="outlined"
                                                />
                                              ))}
                                            </Box>
                                          </Box>
                                        )}

                                        {order.deliveryType === 'Expedited' && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              bgcolor: 'error.light',
                                              color: 'error.contrastText',
                                              borderRadius: '4px',
                                              px: 1,
                                              py: 0.5,
                                              mt: 0.5,
                                              width: 'fit-content'
                                            }}
                                          >
                                            <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                                lineHeight: 1
                                              }}
                                            >
                                              EXPEDITED{order.deliveryTimeline ? ` - ${order.deliveryTimeline}` : ''}
                                              {order.dueDate ? ` (Due: ${new Date(order.dueDate + 'T12:00:00').toLocaleDateString()})` : ''}
                                            </Typography>
                                          </Box>
                                        )}

                                        {/* Additional services */}
                                        {order.additionalServices && Object.values(order.additionalServices).some(val => val) && (
                                          <Box sx={{ mt: 0.5 }}>
                                            {Object.entries(order.additionalServices)
                                              .filter(([_key, value]) => value)
                                              .map(([key]) => (
                                                <Chip
                                                  key={key}
                                                  label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                                                  size="small"
                                                  sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                              ))
                                            }
                                          </Box>
                                        )}
                                      </Box>
                                    </ListItem>
                                  ))}
                              </List>
                            ) : (
                              <Typography variant="body2">No transcript orders.</Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    ))
                  )}
                </Paper>
                
                {/* Payment Information */}
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h3" component="h3" gutterBottom>
                    Payment Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <List dense>
                        {formData.attendanceType && (
                          <ListItem>
                            <ListItemText 
                              primary={<Typography><strong>Attendance Type:</strong> {formData.attendanceType}</Typography>} 
                            />
                          </ListItem>
                        )}
                        {formData.attendanceFee && (
                          <ListItem>
                            <ListItemText 
                              primary={<Typography><strong>Attendance Fee:</strong> ${formData.attendanceFee}</Typography>} 
                            />
                          </ListItem>
                        )}
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <List dense>
                        {(formData.writingTime ?? 0) > 0 && (
                          <ListItem>
                            <ListItemText 
                              primary={<Typography><strong>Writing Time:</strong> {formData.writingTime} hour(s)</Typography>} 
                            />
                          </ListItem>
                        )}
                        {(formData.waitingTime ?? 0) > 0 && (
                          <ListItem>
                            <ListItemText 
                              primary={<Typography><strong>Waiting Time:</strong> {formData.waitingTime} hour(s)</Typography>} 
                            />
                          </ListItem>
                        )}
                      </List>
                    </Grid>
                  </Grid>
                  
                  {/* Payroll Details Section */}
                  {formData.payrollInfo && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Payroll Details:</Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                          {formData.payrollInfo}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                  
                  {/* Special Notes Section */}
                  {formData.specialNotes && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Special Notes:</Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                          {formData.specialNotes}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                  
                  {/* Display invoice files if present (schema-version aware) */}
                  {(() => {
                    const invoiceFiles = getInvoiceFiles(formData);
                    if (invoiceFiles.length === 0) return null;
                    return (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Invoice File(s):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(invoiceFiles as DisplayFile[]).map((file, idx) => (
                            <Chip
                              key={`invoice-${idx}`}
                              label={file.name || file.originalName || file.filename || `Invoice File ${idx + 1}`}
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })()}
                </Paper>
                
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onPrevious}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isSubmitting ? 'Submitting...' : (formData.isCancelled ? 'Confirm Cancellation' : 'Submit Worksheet')}
              </Button>
            </Box>
          </>
        ) : submitResult && !submitResult.success ? (
          // Error state
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 4 
            }}
          >
            <ErrorIcon 
              color="error" 
              sx={{ 
                fontSize: 64, 
                mb: 2 
              }} 
            />
            <Typography variant="h2" component="h2" gutterBottom>
              Submission Failed
            </Typography>
            <Typography variant="body1" paragraph>
              We encountered an error while trying to submit your worksheet.
            </Typography>
            <Alert severity="error" sx={{ my: 3, mx: 'auto', maxWidth: 600 }}>
              {submitResult.error || 'Please try again or contact support if the issue persists.'}
            </Alert>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onPrevious}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isSubmitting ? 'Submitting...' : 'Try Again'}
              </Button>
            </Box>
          </Box>
        ) : (
          // Success state
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 4 
            }}
          >
            <CheckCircleIcon 
              color="success" 
              sx={{ 
                fontSize: 64, 
                mb: 2 
              }} 
            />
            <Typography variant="h2" component="h2" gutterBottom>
              {formData.isCancelled ? 'Cancellation Confirmed' : 'Submission Successful'}
            </Typography>
            
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3, 
                my: 3, 
                maxWidth: 600, 
                mx: 'auto',
                bgcolor: 'background.default'
              }}
            >
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Confirmation Number:
              </Typography>
              <Typography 
                variant="h4" 
                color="primary"
                fontWeight="bold"
                sx={{ mb: 2 }}
              >
                {confirmationNumber}
              </Typography>
              
              <Typography variant="body1" paragraph>
                Thank you for your submission. {formData.isCancelled 
                ? 'We have recorded the cancellation for this job.' 
                : 'Your Reporter Worksheet has been received.'}
              </Typography>
              
              <Typography variant="body1">
                A confirmation email has been sent to {formData.reporterEmail}.
              </Typography>
            </Paper>
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h3" component="h3" gutterBottom>
                Next Steps
              </Typography>
              <Typography variant="body1" paragraph>
                {formData.isCancelled 
                  ? 'No further action is required at this time.' 
                  : 'Our team will process your worksheet and contact you if any additional information is needed.'}
              </Typography>
            </Box>
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                component={MuiLink}
                href="/jobs"
                sx={{ textDecoration: 'none' }}
              >
                Return to Jobs
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => window.print()}
              >
                Print Confirmation
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SubmitConfirmation;